import { useModalAction } from '@components/common/modal/modal.context'
import Button from '@components/ui/button'
import cn from 'classnames'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'
import { FC, useContext, useState } from 'react'
import { UserContext } from '@contexts/user/user.context'
import { IUser } from '@contexts/user/user.utils'
import { toast } from 'react-toastify'
import saveListService from '@services/savelistservice'
import { useMutation } from 'react-query'
import OutlineButton from '@components/ui/outline-button'
import { IoMdAdd } from 'react-icons/io'
import { QuickEntryItem } from '@components/savelist/savelist.utils'
import { getProductsBySkus } from '@framework/product/get-product'
import { Item } from '@contexts/cart/cart.utils'
import { useCart } from '@contexts/cart/cart.context'
import ErrorMessage from './quick-order-error'
import QuickOrderTable from './quick-order-table'
import { ROUTES } from '@utils/routes'

const CreateQuickOrder: FC = () => {
  const { t: translate } = useTranslation(['savelist', 'quickorder'])
  const { user } = useContext(UserContext) as IUser
  const router = useRouter()
  const { closeModal } = useModalAction()
  const [errorSkus, setErrorSkus] = useState<string | null>(null)
  const [isOrderLoading, setIsOrderLoading] = useState(false)
  const [onlyError, setOnlyError] = useState(false)
  const { locale, push } = useRouter()
  const { addOrUpdateItemsHandler } = useCart()
  const [quickEntryList, setQuickEntryList] = useState<QuickEntryItem[]>([])

  const getTypesenseProducts = async () => {
    const skusString = quickEntryList
      .map((item: { sku: string }) => item.sku)
      .join(',')
    let quickEntryListItems: any = quickEntryList
    let selectedListTypesenseItems = await getProductsBySkus(
      skusString,
      quickEntryListItems,
      locale,
    )
    return selectedListTypesenseItems
  }

  const updateQuickEntryItem = (item: QuickEntryItem, itemIndex: number) => {
    setQuickEntryList((prevList) => [
      ...prevList.slice(0, itemIndex),
      item,
      ...prevList.slice(itemIndex + 1),
    ])
  }

  const addItemToNewQuickEntryList = () => {
    setQuickEntryList((prevList) => [
      ...prevList,
      { sku: '', qty: 1, price: 0.0 },
    ])
  }

  const removeItemFromNewQuickEntryList = (indexToRemove: number) => {
    setQuickEntryList((prevList) =>
      prevList.filter((_, index) => index !== indexToRemove),
    )
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<any>()

  const { mutate: createOrder, isLoading } = useMutation(
    ({
      payload,
      selectedListItems,
    }: {
      payload: { cartItems: QuickEntryItem[] }
      selectedListItems: any[]
    }) => {
      return saveListService.createOrderByQuickEntry(payload)
    },
    {
      onSuccess: async (
        response: any,
        { selectedListItems }: { selectedListItems: Item[] },
      ) => {
        if (response?.errorSkus) setErrorSkus(response?.errorSkus)
        if (response?.status) {
          toast.success(response?.message)
          setOnlyError(false)
        } else {
          toast.error(response?.message, {
            position: toast.POSITION.TOP_RIGHT,
            className: 'toast-error',
          })
          setOnlyError(true)
          return
        }
        if (response?.errorSkusList?.length) {
          let updatedSelectedListItems = await selectedListItems.filter(
            (selectedItem) =>
              response.errorSkusList.some(
                (errorItem) => errorItem.sku !== selectedItem.sku,
              ),
          )
          updatedSelectedListItems = updatedSelectedListItems.length
            ? updatedSelectedListItems
            : selectedListItems
          addOrUpdateItemsHandler(updatedSelectedListItems)
        } else {
          addOrUpdateItemsHandler(selectedListItems)
        }
      },
      onError: (err: Error) => {
        toast.error(`Error list updation: ${err?.message}`, {
          position: toast.POSITION.TOP_RIGHT,
          className: 'toast-error',
        })
      },
    },
  )

  const createOrderHandler = async () => {
    const isDuplicationOrDoesNotExist = quickEntryList.find(
      (item) => item.alreadyExists || item.doesNotExists,
    )

    if (isDuplicationOrDoesNotExist) return

    const filteredQuickEntryList = quickEntryList.filter(
      (item) => item.sku.length > 0,
    )
    if (filteredQuickEntryList.length) {
      setErrorSkus(null)
      setIsOrderLoading(true)
      try {
        const items = await getTypesenseProducts()
        setIsOrderLoading(false)
        createOrder({
          payload: { cartItems: filteredQuickEntryList },
          selectedListItems: items, // Ensure this matches the expected type
        })
      } catch (err) {
        console.error(
          'Error fetching typesense products or creating order:',
          err,
        )
        toast.error(err)
        setIsOrderLoading(false) // Ensure loading state is reset on error
      }
    }
  }

  const redirecttoHome = () => push(ROUTES.HOME)

  return (
    <>
      {errorSkus ? (
        <ErrorMessage reportLink={errorSkus} onlyError={onlyError} />
      ) : null}

      <div className={cn('w-full  relative')}>
        <div className="mx-auto overflow-hidden rounded-lg  p-5">
          <QuickOrderTable
            quickEntryList={quickEntryList}
            updateQuickEntryItem={updateQuickEntryItem}
            removeItemFromNewQuickEntryList={removeItemFromNewQuickEntryList}
          />
          <OutlineButton
            className="border-none flex underline my-5"
            onClick={addItemToNewQuickEntryList}
            data-testid="savelist-addsku-popup-button-add-addmoresku"
          >
            {translate('text-add-more-sku')} <IoMdAdd fontSize={18} />
          </OutlineButton>
          <form
            onSubmit={handleSubmit(createOrderHandler)}
            noValidate
            className="mt-3"
          >
            <div className="text-right">
              <OutlineButton
                className="!border-brand rounded text-xs md:text-sm w-20 md:w-36 h-10 border-solid border-[1px] mr-1"
                data-testid="quickorder-actions-section-button-cancel-order"
                onClick={redirecttoHome}
              >
                {translate('text-cancel')}
              </OutlineButton>
              <Button
                className="text-xs md:text-sm  md:w-36 !h-[41px] "
                type="submit"
                data-testid="quickorder-actions-section-button-create-order"
                loading={isLoading || isOrderLoading}
              >
                {translate('text-create', { ns: 'quickorder' })}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default CreateQuickOrder
